import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { divisionFromSearch } from "../lib/drawResultsLink";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import { DrawCeremonyResult } from "../components/DrawCeremonyCard";
import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import { api, ApiError, type DrawCeremony, type TournamentMatch } from "../lib/api";

/** Resultados del sorteo del cuadro (pedido Eduardo 2026-07-15): página
 * propia — el Draw es lo primario en el bracket y aquí vive el acta completa
 * por categoría (hora anunciada, método, bandas sembrado→línea). */
export default function PlayerTournamentDrawCeremonyPage() {
  const { id } = useParams();
  const tournamentId = Number(id);
  const { token } = useAuth();
  // Eduardo 2026-10-05: el aviso «Sorteo … realizado — Categoría 5» llega con
  // ?division=<id> y aquí se ve solo el acta de esa categoría.
  const [searchParams] = useSearchParams();
  const focusDivisionId = divisionFromSearch(searchParams);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tournament, setTournament] = useState<{ id: number; name: string } | null>(null);
  const [matches, setMatches] = useState<TournamentMatch[]>([]);
  const [myParticipantId, setMyParticipantId] = useState<number | null>(null);
  const [ceremonies, setCeremonies] = useState<DrawCeremony[]>([]);
  // Cuenta familiar (Eduardo 2026-09-29): «Tú», tu grupo y tu partido son
  // los del perfil elegido para este torneo.
  const scope = usePlayerProfileScope({ tournamentId });
  const scopeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId) || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await api.resultReports.listTournamentResultsForPlayer(token, tournamentId, scopeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setTournament(response.data.tournament);
      setMatches(response.data.matches);
      setMyParticipantId(response.data.my_participant_id);
      setCeremonies(response.data.draw_ceremonies ?? []);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los resultados del sorteo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, scope.ready, scopeProfileId, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const myDivisionId = useMemo<number | null>(() => {
    if (!myParticipantId) return null;
    const own = matches.find(
      (match) =>
        match.player1_participant_id === myParticipantId || match.player2_participant_id === myParticipantId,
    );
    return own?.division_id ?? null;
  }, [matches, myParticipantId]);

  // Si la categoría de la liga ya no tiene acta (o no se ve), se muestran todas.
  const focusedCeremonies = useMemo(
    () =>
      focusDivisionId == null ? [] : ceremonies.filter((ceremony) => ceremony.division_id === focusDivisionId),
    [ceremonies, focusDivisionId],
  );
  const shownCeremonies = focusedCeremonies.length > 0 ? focusedCeremonies : ceremonies;

  function divisionNameFor(ceremony: DrawCeremony): string {
    if (ceremony.division_id == null) return "Torneo";
    return (
      matches.find((match) => match.division_id === ceremony.division_id)?.division_name?.trim() ||
      `Categoría ${ceremony.division_id}`
    );
  }

  if (Number.isNaN(tournamentId)) {
    return <Navigate to="/player/tournaments" replace />;
  }

  return (
    <>
      {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
          aquí arriba; en celular, la barra flotante de abajo. */}
      <PlayerTournamentBottomNav tournamentId={tournamentId} />
      <PlayerProfileScopeSwitcher scope={scope} tournament />
      <section className="player-games-hero player-games-hero--filter">
        <div>
          <span className="eyebrow">{tournament?.name ?? "Torneo"}</span>
          <h1>Resultados del sorteo</h1>
          <p>Cómo y cuándo se sorteó cada cuadro: sembrados, bandas y líneas, al estándar internacional.</p>
        </div>
        <Link className="btn btn--small" to={`/player/tournaments/${tournamentId}/bracket`}>
          Ver el draw
        </Link>
      </section>

      {error && <div className="alert alert--error">{error}</div>}

      {loading ? (
        <section className="card">
          <p className="muted">Cargando resultados del sorteo…</p>
        </section>
      ) : ceremonies.length === 0 ? (
        <section className="card">
          <p className="muted">Todavía no hay sorteos registrados en este torneo.</p>
        </section>
      ) : (
        shownCeremonies.map((ceremony) => {
          const isMine = ceremony.division_id != null && ceremony.division_id === myDivisionId;
          return (
            <section key={ceremony.id} className="card draw-ceremony">
              <h2 className="card__title">
                {divisionNameFor(ceremony)}
                {isMine ? <small className="muted"> · tu categoría</small> : null}
              </h2>
              <DrawCeremonyResult ceremony={ceremony} />
            </section>
          );
        })
      )}

      {!loading && focusedCeremonies.length > 0 && ceremonies.length > focusedCeremonies.length ? (
        <div className="form__actions">
          <Link className="btn btn--ghost btn--small" to={`/player/tournaments/${tournamentId}/sorteo`}>
            Ver el sorteo de todas las categorías
          </Link>
        </div>
      ) : null}
    </>
  );
}
